import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import {
  ALL_OVERRIDE_ACTIONS,
  ALL_STAGES,
  clearStageOverride,
  getStageOverride,
  listStageOverrideAudit,
  setStageOverride,
  type GovernanceStage,
  type StageOverrideAction,
  type StageOverrideAuditListResponse,
  type StageOverrideCurrent,
} from '../../api/governanceApi';
import { useGovernanceLive } from '../../hooks/useGovernanceLive';
import { ApiError } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';
import '../GovernanceLive/governanceLive.css';
import '../AgentHistory/agentHistory.css';

// 권위: ARCH_DASHBOARD.md v1.8 §4.16.
// - 별도 Redis key `governance:context:override` stamp 방식 (ADR-DASH-05).
// - SET: 2단계 확인 + 비밀번호 재입력. CLEAR: 단일 확인 + 비밀번호.
// - reason 30자+ 필수. NXT 08:00 이전 "장 전" 활성화.

const NXT_OPEN_KST_HOUR = 8;

function todayKstAtHour(h: number): Date {
  // 로컬 오프셋 대신, KST(+09:00) 절대값을 만든다.
  const now = new Date();
  const kstNow = new Date(now.getTime() + 9 * 3600_000);
  const y = kstNow.getUTCFullYear();
  const m = kstNow.getUTCMonth();
  const d = kstNow.getUTCDate();
  return new Date(Date.UTC(y, m, d, h - 9, 0, 0));
}

function formatKst(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? '—';
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

export default function StageOverridePage(): JSX.Element {
  const qc = useQueryClient();
  const [setModalOpen, setSetModalOpen] = useState(false);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const currentQuery = useQuery<StageOverrideCurrent, ApiError>({
    queryKey: ['governance', 'stage-override', 'current'],
    queryFn: getStageOverride,
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: false,
  });

  const auditQuery = useQuery<StageOverrideAuditListResponse, ApiError>({
    queryKey: ['governance', 'stage-override', 'audit'],
    queryFn: () => listStageOverrideAudit({ page: 0, size: 100 }),
    staleTime: 60_000,
    retry: false,
  });

  const onLive = useCallback(
    (ev: { event_code: string }) => {
      if (
        ev.event_code === 'GOVERNANCE.MANUAL.OVERRIDE_SET' ||
        ev.event_code === 'GOVERNANCE.MANUAL.OVERRIDE_CLEARED' ||
        ev.event_code === 'GOVERNANCE.CONTEXT.PUBLISHED'
      ) {
        qc.invalidateQueries({ queryKey: ['governance', 'stage-override'] });
      }
    },
    [qc],
  );
  useGovernanceLive(onLive);

  // NXT 08:00 이전 활성 여부 (클라이언트 타이머, 매 초 tick)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const nxtOpen = useMemo(() => todayKstAtHour(NXT_OPEN_KST_HOUR), []);
  const canSet = now < nxtOpen.getTime();
  const msUntilNxt = Math.max(0, nxtOpen.getTime() - now);

  const setMut = useMutation({
    mutationFn: setStageOverride,
    onSuccess: () => {
      setSetModalOpen(false);
      qc.invalidateQueries({ queryKey: ['governance', 'stage-override'] });
    },
  });

  const clearMut = useMutation({
    mutationFn: clearStageOverride,
    onSuccess: () => {
      setClearModalOpen(false);
      qc.invalidateQueries({ queryKey: ['governance', 'stage-override'] });
    },
  });

  const current = currentQuery.data;
  const auditItems = auditQuery.data?.items ?? [];
  const selected = useMemo(
    () => auditItems.find((a) => a.audit_id === selectedAudit) ?? null,
    [auditItems, selectedAudit],
  );

  return (
    <div className="gov-page">
      <header className="gov-page__header">
        <h1 className="gov-page__title">수동 Stage 재정의</h1>
        <div className="gov-page__actions">
          <Link to="/governance/live">실시간 현황</Link>
          <Link to="/governance/sla">SLA 감시</Link>
        </div>
      </header>

      {/* 안전 가이드 배너 */}
      <div className="gov-warning" role="note">
        <AlertTriangle
          size={14}
          strokeWidth={1.75}
          style={{ verticalAlign: 'text-bottom', marginRight: 6 }}
        />
        <strong>경고:</strong> 수동 Stage 재정의는 극단 상황 대응 경로입니다. 다음 장 전 거버넌스 배치(07:00 KST)가
        정상 완료되면 자동 해제됩니다. 감사 로그는 2년간 보관됩니다.
        {!canSet && (
          <>
            {' '}
            현재 NXT 개장(08:00 KST) 이후 — <strong>SET 비활성</strong>. CLEAR 는 상시 가능.
          </>
        )}
      </div>

      {currentQuery.isLoading && (
        <div className="gov-muted" style={{ padding: 24 }}>상태 조회 중…</div>
      )}
      {currentQuery.isError && (
        <div className="gov-error" role="alert">
          재정의 상태 조회 실패: {currentQuery.error?.message ?? 'unknown'}
        </div>
      )}

      {/* 현재 상태 배너 */}
      {current && (
        <section
          className={`gov-banner ${current.active ? 'gov-banner--warn' : 'gov-banner--ok'}`}
        >
          <div className="gov-banner__left">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h2 className="gov-banner__title">
                {current.active ? 'SUSPENDED — 재정의 활성' : 'NORMAL — 재정의 없음'}
              </h2>
              {current.active ? (
                <>
                  <div className="gov-banner__sub">
                    Stage <strong>{current.stage_override}</strong>
                    {current.current_context && (
                      <>
                        {' '}(원본 {String(current.current_context.stage)})
                      </>
                    )}
                    · max_invest_ratio{' '}
                    <strong>
                      {current.ratio_override !== null
                        ? `${(current.ratio_override * 100).toFixed(0)}%`
                        : '—'}
                    </strong>
                  </div>
                  <div className="gov-banner__sub">
                    set_by {current.set_by ?? '—'} · set_at {formatKst(current.set_at)} ·
                    expires_at {formatKst(current.expires_at)}
                  </div>
                  {current.reason && (
                    <div className="gov-override-card__rationale">“{current.reason}”</div>
                  )}
                </>
              ) : (
                <div className="gov-banner__sub">거버넌스 에이전트 판정이 그대로 적용 중.</div>
              )}
            </div>
          </div>
          <div className="gov-banner__right">
            {current.active ? (
              <button
                type="button"
                className="gov-btn gov-btn--danger"
                onClick={() => setClearModalOpen(true)}
              >
                CLEAR (해제)
              </button>
            ) : (
              <button
                type="button"
                className="gov-btn gov-btn--primary"
                onClick={() => setSetModalOpen(true)}
                disabled={!canSet}
                title={!canSet ? 'NXT 개장 이후에는 SET 비활성' : undefined}
              >
                SET (재정의 등록)
              </button>
            )}
          </div>
        </section>
      )}

      <div className="gov-countdown">
        NXT 08:00 KST 까지{' '}
        {(() => {
          const sec = Math.floor(msUntilNxt / 1000);
          const h = Math.floor(sec / 3600);
          const m = Math.floor((sec % 3600) / 60);
          const s = sec % 60;
          return `${h}h ${m}m ${s}s`;
        })()}
      </div>

      {/* 섹션 B — 감사 이력 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <section className="gov-card" style={{ padding: 0 }}>
          <div style={{ padding: 16 }}>
            <h2 className="gov-card__title" style={{ margin: 0 }}>
              섹션 B · 재정의 감사 이력 (최근 100건)
            </h2>
          </div>
          {auditQuery.isError && (
            <div className="gov-error" style={{ margin: 8 }}>
              감사 이력 조회 실패: {auditQuery.error?.message ?? 'unknown'}
            </div>
          )}
          <table className="gov-table">
            <thead>
              <tr>
                <th>action_at</th>
                <th>action</th>
                <th>set_by</th>
                <th>stage</th>
                <th>ratio</th>
                <th>reason</th>
              </tr>
            </thead>
            <tbody>
              {auditQuery.isLoading && (
                <tr>
                  <td colSpan={6} className="gov-muted" style={{ padding: 16 }}>
                    로딩 중…
                  </td>
                </tr>
              )}
              {!auditQuery.isLoading && auditItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="gov-muted" style={{ padding: 16 }}>
                    감사 이력이 없습니다.
                  </td>
                </tr>
              )}
              {auditItems.map((a) => (
                <tr
                  key={a.audit_id}
                  onClick={() => setSelectedAudit(a.audit_id)}
                  style={{ cursor: 'pointer' }}
                  aria-selected={selectedAudit === a.audit_id}
                >
                  <td>{formatKst(a.action_at)}</td>
                  <td>
                    <code>{a.action}</code>
                  </td>
                  <td>{a.set_by ?? '—'}</td>
                  <td>
                    {renderStage(a.stage_before)} → {renderStage(a.stage_after)}
                  </td>
                  <td className="num">
                    {formatRatio(a.ratio_before)} → {formatRatio(a.ratio_after)}
                  </td>
                  <td
                    style={{
                      maxWidth: 260,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {a.reason ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* 상세 */}
        <section className="gov-card">
          <h2 className="gov-card__title">상세</h2>
          {!selected && (
            <div className="gov-muted">좌측 테이블에서 감사 row 를 선택하세요.</div>
          )}
          {selected && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <span className="gov-kpi__label">audit_id</span>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                  {selected.audit_id}
                </div>
              </div>
              <div>
                <span className="gov-kpi__label">action</span>
                <div>
                  <code>{selected.action}</code>
                </div>
              </div>
              <div>
                <span className="gov-kpi__label">before → after</span>
                <div className="gov-diff">
                  <div className="gov-diff__block">
                    <span className="gov-diff__label">before</span>
                    <span className="gov-diff__value">
                      stage {renderStage(selected.stage_before)} / ratio{' '}
                      {formatRatio(selected.ratio_before)}
                    </span>
                  </div>
                  <div className="gov-diff__block">
                    <span className="gov-diff__label">after</span>
                    <span className="gov-diff__value">
                      stage {renderStage(selected.stage_after)} / ratio{' '}
                      {formatRatio(selected.ratio_after)}
                    </span>
                  </div>
                </div>
              </div>
              {selected.reason && (
                <div>
                  <span className="gov-kpi__label">reason</span>
                  <div>{selected.reason}</div>
                </div>
              )}
              {selected.expires_at && (
                <div>
                  <span className="gov-kpi__label">expires_at</span>
                  <div>{formatKst(selected.expires_at)}</div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {setModalOpen && current && (
        <SetOverrideModal
          current={current}
          defaultExpiresAt={new Date(nxtOpen.getTime()).toISOString()}
          onClose={() => setSetModalOpen(false)}
          onConfirm={(body) =>
            setMut.mutateAsync({
              approver: useAuthStore.getState().user?.email ?? 'operator',
              ...body,
            })
          }
          busy={setMut.isPending}
          error={setMut.isError ? (setMut.error as ApiError | Error)?.message : null}
        />
      )}

      {clearModalOpen && (
        <ClearOverrideModal
          onClose={() => setClearModalOpen(false)}
          onConfirm={(reason, password) =>
            clearMut.mutateAsync({
              approver: useAuthStore.getState().user?.email ?? 'operator',
              reason,
              password,
            })
          }
          busy={clearMut.isPending}
          error={clearMut.isError ? (clearMut.error as ApiError | Error)?.message : null}
        />
      )}
    </div>
  );
}

function renderStage(s: string | null | undefined): JSX.Element | string {
  if (!s) return '—';
  return (
    <span className={`gov-hist-stage gov-hist-stage--${String(s).toLowerCase()}`}>
      {s}
    </span>
  );
}

function formatRatio(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(0)}%`;
}

// ── SET 모달 (2단계) ────────────────────────────────
function SetOverrideModal({
  current,
  defaultExpiresAt,
  onClose,
  onConfirm,
  busy,
  error,
}: {
  current: StageOverrideCurrent;
  defaultExpiresAt: string;
  onClose: () => void;
  onConfirm: (body: {
    reason: string;
    stage_override: GovernanceStage;
    ratio_override: number;
    password: string;
  }) => Promise<unknown>;
  busy?: boolean;
  error?: string | null;
}): JSX.Element {
  const [step, setStep] = useState<1 | 2>(1);
  const originalStage =
    (current.current_context?.stage as GovernanceStage | undefined) ?? 'B';
  const originalRatio = current.current_context?.max_invest_ratio ?? 0.3;

  const [stage, setStage] = useState<GovernanceStage>(originalStage);
  const [ratio, setRatio] = useState<number>(originalRatio);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState(
    defaultExpiresAt.slice(0, 16), // datetime-local 형식
  );
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (step === 1) reasonRef.current?.focus();
  }, [step]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const reasonOk = reason.trim().length >= 30;

  return (
    <div
      className="gov-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="수동 Stage 재정의 — 적용"
    >
      <div className="gov-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="gov-modal__title">
            {step === 1
              ? 'Stage 재정의 — 변경 내용 확인'
              : 'Stage 재정의 — 비밀번호 재입력'}
          </h2>
          <button type="button" className="gov-btn" onClick={onClose} disabled={busy}>
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>

        {step === 1 && (
          <>
            <div className="gov-diff">
              <div className="gov-diff__block">
                <span className="gov-diff__label">원본</span>
                <span className="gov-diff__value">
                  stage {renderStage(originalStage)} / ratio {(originalRatio * 100).toFixed(0)}%
                </span>
              </div>
              <div className="gov-diff__block">
                <span className="gov-diff__label">재정의</span>
                <span className="gov-diff__value">
                  stage {renderStage(stage)} / ratio {(ratio * 100).toFixed(0)}%
                </span>
              </div>
            </div>
            <div>
              <span className="gov-modal__label">재정의 Stage</span>
              <div className="gov-chip-row">
                {ALL_STAGES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`gov-chip${stage === s ? ' gov-chip--active' : ''}`}
                    onClick={() => setStage(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="gov-modal__label">
                재정의 max_invest_ratio ({(ratio * 100).toFixed(0)}%)
              </span>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={Math.round(ratio * 100)}
                onChange={(e) => setRatio(Number(e.target.value) / 100)}
                disabled={busy}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <span className="gov-modal__label">
                이유 (30자 이상 필수 · {reason.trim().length}/30+)
              </span>
              <textarea
                ref={reasonRef}
                className="gov-modal__textarea"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="예: K-VIX 48 급등 + 야간 선물 급락 — Stage 하향 필요"
                disabled={busy}
              />
            </div>
            <div>
              <span className="gov-modal__label">expires_at (기본: 다음 NXT 개장)</span>
              <input
                type="datetime-local"
                className="gov-modal__input"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="gov-modal__actions">
              <button type="button" className="gov-btn" onClick={onClose} disabled={busy}>
                취소
              </button>
              <button
                type="button"
                className="gov-btn gov-btn--primary"
                disabled={!reasonOk || busy}
                onClick={() => setStep(2)}
              >
                다음
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="gov-warning">
              2단계 확인 — 운영자 비밀번호를 재입력하세요.
            </div>
            <label>
              <span className="gov-modal__label">비밀번호</span>
              <input
                type="password"
                className="gov-modal__input"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </label>
            {error && <div className="gov-error">{error}</div>}
            <div className="gov-modal__actions">
              <button type="button" className="gov-btn" onClick={() => setStep(1)} disabled={busy}>
                뒤로
              </button>
              <button
                type="button"
                className="gov-btn gov-btn--danger"
                disabled={!password || busy}
                onClick={() =>
                  onConfirm({
                    reason: reason.trim(),
                    stage_override: stage,
                    ratio_override: ratio,
                    password,
                  }).catch(() => {})
                }
              >
                {busy ? '적용 중…' : '적용'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── CLEAR 모달 (단일 확인 + 비밀번호) ───────────────
function ClearOverrideModal({
  onClose,
  onConfirm,
  busy,
  error,
}: {
  onClose: () => void;
  onConfirm: (reason: string, password: string) => Promise<unknown>;
  busy?: boolean;
  error?: string | null;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const reasonOk = reason.trim().length >= 10; // CLEAR 는 완화
  return (
    <div
      className="gov-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Stage 재정의 — 해제"
    >
      <div className="gov-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="gov-modal__title">Stage 재정의 — CLEAR</h2>
          <button type="button" className="gov-btn" onClick={onClose} disabled={busy}>
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>
        <div className="gov-warning">현재 활성 재정의를 해제합니다. 감사 테이블에 기록됩니다.</div>
        <label>
          <span className="gov-modal__label">
            해제 사유 (10자 이상)
          </span>
          <textarea
            className="gov-modal__textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            autoFocus
          />
        </label>
        <label>
          <span className="gov-modal__label">비밀번호</span>
          <input
            type="password"
            className="gov-modal__input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </label>
        {error && <div className="gov-error">{error}</div>}
        <div className="gov-modal__actions">
          <button type="button" className="gov-btn" onClick={onClose} disabled={busy}>
            취소
          </button>
          <button
            type="button"
            className="gov-btn gov-btn--danger"
            disabled={!reasonOk || !password || busy}
            onClick={() => onConfirm(reason.trim(), password).catch(() => {})}
          >
            {busy ? '해제 중…' : 'CLEAR 확인'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ALL_OVERRIDE_ACTIONS 를 참조 (lint 무해화용 — 미래 필터 확장 대비).
void ALL_OVERRIDE_ACTIONS;
void ([] as StageOverrideAction[]);
